import { Toaster } from "react-hot-toast";
import Layout from "../shared/Layout";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { API_URL, baseurl } from "../../_config";

const matchStatusOptions = ["upcoming", "active", "live", "completed", "cancelled"];
const gameStatusOptions = ["pending", "live", "completed"];

function getScoreValue(value) {
    const score = Number(value || 0);
    return Number.isNaN(score) || score < 0 ? 0 : score;
}

function getPlayerNameList(players) {
    if (!players?.length) return "";
    return players.map((player) => player.playerName).filter(Boolean).join(" / ");
}

function sameIds(first = [], second = []) {
    const a = [...first].map(Number).sort((left, right) => left - right);
    const b = [...second].map(Number).sort((left, right) => left - right);
    return a.length === b.length && a.every((id, index) => id === b[index]);
}

function normalizeGameFlow(games = []) {
    let hasOpenRound = false;

    return [...games]
        .sort((first, second) => Number(first.game_no) - Number(second.game_no))
        .map((game) => {
            const nextGame = { ...game };

            if (nextGame.status === "completed") return nextGame;

            if (!hasOpenRound) {
                hasOpenRound = true;
                nextGame.status = nextGame.status === "live" ? "live" : "pending";
                return nextGame;
            }

            nextGame.status = "pending";
            return nextGame;
        });
}

function getEditableRoundNo(games = []) {
    const nextRound = [...games]
        .sort((first, second) => Number(first.game_no) - Number(second.game_no))
        .find((game) => game.status !== "completed");

    return nextRound ? Number(nextRound.game_no) : null;
}

function getRoundStatusOptions(game, editableRoundNo) {
    if (game.status === "completed") return ["completed"];
    if (Number(game.game_no) === Number(editableRoundNo)) return gameStatusOptions;
    return ["pending"];
}

const YblScorecardPage = () => {
    const { matchId } = useParams();
    const [scorecard, setScorecard] = useState(null);
    const [matchStatus, setMatchStatus] = useState("upcoming");
    const [selectedTeamA, setSelectedTeamA] = useState([]);
    const [selectedTeamB, setSelectedTeamB] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchScorecard = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${baseurl}${API_URL.getYblMatchScorecard}?matchId=${matchId}`);
            const data = res.data?.data || null;
            setScorecard(data);
            setMatchStatus(data?.match?.status || "upcoming");
            setSelectedTeamA((data?.teamAPlayers || []).map((player) => Number(player.player_id || player.id)));
            setSelectedTeamB((data?.teamBPlayers || []).map((player) => Number(player.player_id || player.id)));
        } catch {
            toast.error("Failed to load match scorecard");
        } finally {
            setLoading(false);
        }
    }, [matchId]);

    useEffect(() => {
        fetchScorecard();
    }, [fetchScorecard]);

    const match = scorecard?.match || {};
    const games = normalizeGameFlow(scorecard?.games || []);
    const summary = scorecard?.summary || {};
    const teamAOptions = scorecard?.teamAAvailablePlayers || [];
    const teamBOptions = scorecard?.teamBAvailablePlayers || [];
    const savedTeamA = (scorecard?.teamAPlayers || []).map((player) => Number(player.player_id || player.id));
    const savedTeamB = (scorecard?.teamBPlayers || []).map((player) => Number(player.player_id || player.id));
    const selectedAPlayers = teamAOptions.filter((player) => selectedTeamA.includes(Number(player.id)));
    const selectedBPlayers = teamBOptions.filter((player) => selectedTeamB.includes(Number(player.id)));
    const teamADropdownOptions = teamAOptions.filter((player) => !selectedTeamA.includes(Number(player.id)));
    const teamBDropdownOptions = teamBOptions.filter((player) => !selectedTeamB.includes(Number(player.id)));
    const teamADisplayName = getPlayerNameList(selectedAPlayers) || getPlayerNameList(scorecard?.teamAPlayers) || match.participantA_name || "Team A";
    const teamBDisplayName = getPlayerNameList(selectedBPlayers) || getPlayerNameList(scorecard?.teamBPlayers) || match.participantB_name || "Team B";
    const lineupReady = selectedTeamA.length > 0 && selectedTeamB.length > 0;
    const lineupSaved = Boolean(scorecard?.lineupReady) && sameIds(selectedTeamA, savedTeamA) && sameIds(selectedTeamB, savedTeamB);
    const editableRoundNo = getEditableRoundNo(games);
    const isTeamAWinner = summary.matchWinnerId && Number(summary.matchWinnerId) === Number(match.participantA_id);
    const isTeamBWinner = summary.matchWinnerId && Number(summary.matchWinnerId) === Number(match.participantB_id);

    const winnerName = summary.matchWinnerId && Number(summary.matchWinnerId) === Number(match.participantA_id)
        ? match.participantA_name
        : summary.matchWinnerId && Number(summary.matchWinnerId) === Number(match.participantB_id)
            ? match.participantB_name
            : "-";

    const savePlayerSelectionToApi = async (teamAPlayerIds = selectedTeamA, teamBPlayerIds = selectedTeamB, showToast = false) => {
        setSaving(true);
        try {
            const res = await axios.post(`${baseurl}${API_URL.saveYblMatchPlayers}`, {
                matchId,
                teamAPlayerIds,
                teamBPlayerIds,
            });

            if (res.data?.isSuccess === 1) {
                if (showToast) toast.success(res.data.message || "Playing players saved");
                setScorecard(res.data.data);
                setMatchStatus(res.data.data?.match?.status || matchStatus);
                return res.data.data;
            }

            toast.error(res.data?.message || "Player selection failed");
        } catch (error) {
            toast.error(error.response?.data?.message || "Error saving players");
        } finally {
            setSaving(false);
        }

        return null;
    };

    const addPlayer = (side, playerId) => {
        const id = Number(playerId);
        if (!id) return;

        if (side === "A") {
            const nextTeamA = selectedTeamA.includes(id) ? selectedTeamA : [...selectedTeamA, id];
            setSelectedTeamA(nextTeamA);
            savePlayerSelectionToApi(nextTeamA, selectedTeamB);
            return;
        }

        const nextTeamB = selectedTeamB.includes(id) ? selectedTeamB : [...selectedTeamB, id];
        setSelectedTeamB(nextTeamB);
        savePlayerSelectionToApi(selectedTeamA, nextTeamB);
    };

    const removePlayer = (side, playerId) => {
        const id = Number(playerId);
        if (side === "A") {
            const nextTeamA = selectedTeamA.filter((item) => Number(item) !== id);
            setSelectedTeamA(nextTeamA);
            savePlayerSelectionToApi(nextTeamA, selectedTeamB);
            return;
        }
        const nextTeamB = selectedTeamB.filter((item) => Number(item) !== id);
        setSelectedTeamB(nextTeamB);
        savePlayerSelectionToApi(selectedTeamA, nextTeamB);
    };

    const saveScorecard = async (statusOverride, gamesOverride, showToast = false) => {
        if (!scorecard) return;
        const nextStatus = statusOverride || matchStatus;

        if (["active", "live", "completed"].includes(nextStatus) && !lineupReady) {
            toast.error("Select playing players for both teams before starting match");
            return;
        }
        if (["active", "live", "completed"].includes(nextStatus) && !lineupSaved) {
            const savedSelection = await savePlayerSelectionToApi(selectedTeamA, selectedTeamB, false);
            if (!savedSelection) return;
        }

        setSaving(true);
        try {
            const res = await axios.post(`${baseurl}${API_URL.saveYblMatchScorecard}`, {
                matchId,
                status: nextStatus,
                games: normalizeGameFlow(gamesOverride || scorecard.games),
            });

            if (res.data?.isSuccess === 1) {
                if (showToast) toast.success(res.data.message || "Scorecard saved");
                setScorecard(res.data.data);
                setMatchStatus(res.data.data?.match?.status || statusOverride || matchStatus);
            } else {
                toast.error(res.data?.message || "Scorecard save failed");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Error saving scorecard");
        } finally {
            setSaving(false);
        }
    };

    const updateGameScore = async (gameNo, field, value) => {
        const nextGames = games.map((game) => (
            Number(game.game_no) === Number(gameNo)
                ? { ...game, [field]: getScoreValue(value) }
                : game
        ));
        setScorecard((prev) => ({ ...prev, games: nextGames }));
        await saveScorecard(matchStatus === "upcoming" ? "live" : matchStatus, nextGames);
    };

    const updateRoundStatus = async (gameNo, status) => {
        const targetRound = games.find((game) => Number(game.game_no) === Number(gameNo));
        if (!targetRound) return;

        if (status === "completed" && getScoreValue(targetRound.teamA_score) === getScoreValue(targetRound.teamB_score)) {
            toast.error("Round score cannot be tied before completing");
            return;
        }

        const nextGames = normalizeGameFlow(games.map((game) => (
            Number(game.game_no) === Number(gameNo)
                ? { ...game, status }
                : game
        )));
        const nextMatchStatus = nextGames.every((game) => game.status === "completed")
            ? "completed"
            : nextGames.some((game) => game.status === "live" || game.status === "completed")
                ? "live"
                : matchStatus;

        setScorecard((prev) => ({ ...prev, games: nextGames }));
        setMatchStatus(nextMatchStatus);
        await saveScorecard(nextMatchStatus, nextGames, true);
    };

    const adjustPoint = async (gameNo, team, delta) => {
        if (!lineupSaved) {
            toast.error("Save playing players before live scoring");
            return;
        }

        setSaving(true);
        try {
            const res = await axios.post(`${baseurl}${API_URL.adjustYblMatchPoint}`, {
                matchId,
                gameNo,
                team,
                delta,
            });

            if (res.data?.isSuccess === 1) {
                setScorecard(res.data.data);
                setMatchStatus(res.data.data?.match?.status || "live");
            } else {
                toast.error(res.data?.message || "Score update failed");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Error updating point");
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async (value) => {
        if (["active", "live", "completed"].includes(value) && !lineupReady) {
            toast.error("Select playing players for both teams before changing match status");
            return;
        }
        setMatchStatus(value);
        await saveScorecard(value, games, true);
    };

    return (
        <Layout>
            <Toaster position="top-right" />
            <div className="right-content-section ybl-scorecard-page">
                <div className="admin-page mt-3 d-sm-flex justify-content-between align-items-center">
                    <div className="admin-heading d-flex align-items-center gap-3">
                        <Link to="/ybl-matches" className="back_button">
                            <i className="fas fa-arrow-left"></i>
                        </Link>
                        <h4 className="mb-0">YBL Match Scorecard</h4>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <select
                            value={matchStatus}
                            onChange={(event) => handleStatusChange(event.target.value)}
                            className="form-control ybl-score-status"
                        >
                            {matchStatusOptions.map((status) => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div className="datatable mt-4 p-4">Loading...</div>
                ) : scorecard ? (
                    <>
                        <div className="ybl-score-hero mt-4">
                            <div>
                                <span>{match.tournamentName || "Tournament"}</span>
                                <h3>{teamADisplayName} vs {teamBDisplayName}</h3>
                                <p>{match.seasonName || "-"} | {match.round || "-"} | Court {match.court || "-"}</p>
                                <p className="mb-0">{match.participantA_name || "Team A"} vs {match.participantB_name || "Team B"}</p>
                            </div>
                            <div className="ybl-score-summary">
                                <div>
                                    <span>{teamADisplayName}</span>
                                    {isTeamAWinner && <b>Win</b>}
                                    <strong>{summary.teamAGamesWon || 0}</strong>
                                </div>
                                <div>
                                    <span>{teamBDisplayName}</span>
                                    {isTeamBWinner && <b>Win</b>}
                                    <strong>{summary.teamBGamesWon || 0}</strong>
                                </div>
                                <small>Winner: {winnerName}</small>
                            </div>
                        </div>

                        <div className="ybl-roster-grid mt-3">
                            <div className="ybl-roster-panel">
                                <h5>{match.participantA_name || "Team A"} Playing Players</h5>
                                <div className="ybl-player-dropdown-panel">
                                    <select
                                        className="form-control"
                                        value=""
                                        onChange={(event) => addPlayer("A", event.target.value)}
                                        disabled={saving || !teamAOptions.length}
                                    >
                                        <option value="">Select {match.participantA_name || "Team A"} Player</option>
                                        {teamADropdownOptions.map((player) => (
                                            <option key={player.id} value={player.id}>
                                                {player.playerName}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="ybl-selected-player-list">
                                        {selectedAPlayers.map((player) => (
                                            <span key={player.id}>
                                                {player.playerName}
                                                <button type="button" onClick={() => removePlayer("A", player.id)} disabled={saving}>x</button>
                                            </span>
                                        ))}
                                        {!teamAOptions.length && <small>No players assigned to this team</small>}
                                        {teamAOptions.length > 0 && !selectedAPlayers.length && <small>No players selected</small>}
                                    </div>
                                </div>
                            </div>
                            <div className="ybl-roster-panel">
                                <h5>{match.participantB_name || "Team B"} Playing Players</h5>
                                <div className="ybl-player-dropdown-panel">
                                    <select
                                        className="form-control"
                                        value=""
                                        onChange={(event) => addPlayer("B", event.target.value)}
                                        disabled={saving || !teamBOptions.length}
                                    >
                                        <option value="">Select {match.participantB_name || "Team B"} Player</option>
                                        {teamBDropdownOptions.map((player) => (
                                            <option key={player.id} value={player.id}>
                                                {player.playerName}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="ybl-selected-player-list">
                                        {selectedBPlayers.map((player) => (
                                            <span key={player.id}>
                                                {player.playerName}
                                                <button type="button" onClick={() => removePlayer("B", player.id)} disabled={saving}>x</button>
                                            </span>
                                        ))}
                                        {!teamBOptions.length && <small>No players assigned to this team</small>}
                                        {teamBOptions.length > 0 && !selectedBPlayers.length && <small>No players selected</small>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="datatable ybl-score-table mt-4">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Round</th>
                                        <th>{teamADisplayName}</th>
                                        <th>{teamBDisplayName}</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {games.map((game) => (
                                        <tr key={game.game_no} className={game.status === "completed" ? "ybl-round-completed" : Number(game.game_no) !== Number(editableRoundNo) ? "ybl-round-disabled" : ""}>
                                            <td>Round {game.game_no}</td>
                                            <td>
                                                <div className="ybl-score-control">
                                                    <button onClick={() => adjustPoint(game.game_no, "A", -1)} disabled={saving || !lineupSaved || game.status === "completed" || Number(game.game_no) !== Number(editableRoundNo)}>-</button>
                                                    <input
                                                        type="number"
                                                        value={getScoreValue(game.teamA_score)}
                                                        disabled={saving || !lineupSaved || game.status === "completed" || Number(game.game_no) !== Number(editableRoundNo)}
                                                        onChange={(event) => updateGameScore(game.game_no, "teamA_score", event.target.value)}
                                                    />
                                                    <button onClick={() => adjustPoint(game.game_no, "A", 1)} disabled={saving || !lineupSaved || game.status === "completed" || Number(game.game_no) !== Number(editableRoundNo)}>+</button>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="ybl-score-control">
                                                    <button onClick={() => adjustPoint(game.game_no, "B", -1)} disabled={saving || !lineupSaved || game.status === "completed" || Number(game.game_no) !== Number(editableRoundNo)}>-</button>
                                                    <input
                                                        type="number"
                                                        value={getScoreValue(game.teamB_score)}
                                                        disabled={saving || !lineupSaved || game.status === "completed" || Number(game.game_no) !== Number(editableRoundNo)}
                                                        onChange={(event) => updateGameScore(game.game_no, "teamB_score", event.target.value)}
                                                    />
                                                    <button onClick={() => adjustPoint(game.game_no, "B", 1)} disabled={saving || !lineupSaved || game.status === "completed" || Number(game.game_no) !== Number(editableRoundNo)}>+</button>
                                                </div>
                                            </td>
                                            <td>
                                                <select
                                                    className="form-control"
                                                    value={game.status || "pending"}
                                                    disabled={saving || !lineupSaved || game.status === "completed" || Number(game.game_no) !== Number(editableRoundNo)}
                                                    onChange={(event) => updateRoundStatus(game.game_no, event.target.value)}
                                                >
                                                    {getRoundStatusOptions(game, editableRoundNo).map((status) => (
                                                        <option key={status} value={status}>{status}</option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </>
                ) : (
                    <div className="datatable mt-4 p-4">Scorecard not found</div>
                )}
            </div>
        </Layout>
    );
};

export default YblScorecardPage;
